// src/pages/HowToUse.jsx — Epic Games Store style
import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/common/Navbar";
import { useLang } from "../context/LanguagesContext";

const S = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
*, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
.htu { min-height:100vh; background:#121212; color:#fff; font-family:'Inter',sans-serif; }

/* ── Hero ── */
.htu-hero {
  padding:100px 80px 48px; text-align:center;
  background:linear-gradient(180deg,rgba(0,120,242,0.06) 0%,transparent 100%);
  border-bottom:1px solid rgba(255,255,255,0.06);
}
.htu-tag { display:inline-block; font-size:11px; font-weight:700; color:#0078F2; text-transform:uppercase; letter-spacing:1.5px; background:rgba(0,120,242,0.1); border:1px solid rgba(0,120,242,0.25); border-radius:4px; padding:4px 12px; margin-bottom:16px; }
.htu-title { font-size:clamp(30px,4.5vw,52px); font-weight:900; color:#fff; letter-spacing:-1.5px; margin-bottom:12px; line-height:1.05; }
.htu-title span { color:#0078F2; }
.htu-sub { font-size:15px; color:#6b6b6b; max-width:440px; margin:0 auto; line-height:1.65; }

/* ── Videos ── */
.vids-wrap { max-width:1100px; margin:0 auto; padding:48px 80px; }
.sect-tag-sm { font-size:11px; font-weight:700; color:#0078F2; text-transform:uppercase; letter-spacing:1.5px; margin-bottom:8px; }
.sect-title-sm { font-size:clamp(20px,2.5vw,28px); font-weight:900; color:#fff; letter-spacing:-0.5px; margin-bottom:24px; }
.vids-grid { display:grid; grid-template-columns:1fr 1fr; gap:20px; }
.vid-card { background:#1a1a1a; border:1px solid rgba(255,255,255,0.08); border-radius:12px; overflow:hidden; transition:border-color 0.15s; }
.vid-card:hover { border-color:rgba(0,120,242,0.35); }
.vid-label { display:flex; align-items:center; gap:10px; padding:14px 16px 10px; }
.vid-label-icon { width:30px; height:30px; border-radius:6px; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
.vid-label-title { font-size:13px; font-weight:700; color:#fff; }
.vid-label-sub { font-size:11px; color:#6b6b6b; margin-top:1px; }
.vid-frame-wrap { position:relative; width:100%; aspect-ratio:16/9; background:#0d0d0d; }
.vid-frame-wrap iframe { position:absolute; inset:0; width:100%; height:100%; border:none; }

/* ── Steps — 2-col grid ── */
.htu-steps-bg { background:#1a1a1a; border-top:1px solid rgba(255,255,255,0.06); border-bottom:1px solid rgba(255,255,255,0.06); }
.htu-steps-wrap { max-width:1100px; margin:0 auto; padding:48px 80px; }
.steps-grid { display:grid; grid-template-columns:1fr 1fr; }
.step-block { display:flex; gap:14px; padding:20px; border-bottom:1px solid rgba(255,255,255,0.05); border-right:1px solid rgba(255,255,255,0.05); }
.step-block:nth-child(even) { border-right:none; }
.step-block:nth-last-child(1),.step-block:nth-last-child(2) { border-bottom:none; }
.step-left { display:flex; flex-direction:column; align-items:center; flex-shrink:0; gap:4px; }
.step-circle { width:40px; height:40px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:17px; border:1px solid; flex-shrink:0; }
.step-num-label { font-size:10px; font-weight:700; letter-spacing:0.5px; }
.step-right { flex:1; padding-top:4px; }
.step-title { font-size:14px; font-weight:800; color:#fff; margin-bottom:4px; }
.step-desc { font-size:12px; color:#6b6b6b; line-height:1.6; margin-bottom:8px; }
.step-details { display:flex; flex-direction:column; gap:3px; }
.step-detail { display:flex; gap:7px; font-size:11px; color:#4a4a4a; line-height:1.5; }
.step-arrow { font-weight:700; flex-shrink:0; }

/* ── FAQ ── */
.faq-wrap { background:#121212; border-top:1px solid rgba(255,255,255,0.06); }
.faq-inner { max-width:860px; margin:0 auto; padding:48px 80px; }
.faq-heading { font-size:clamp(20px,2.5vw,26px); font-weight:900; color:#fff; letter-spacing:-0.5px; margin-bottom:20px; }
.faq-item { border:1px solid rgba(255,255,255,0.07); border-radius:8px; overflow:hidden; margin-bottom:6px; transition:border-color 0.15s; }
.faq-item:hover { border-color:rgba(255,255,255,0.12); }
.faq-q { padding:13px 16px; font-size:14px; font-weight:600; color:#e0e0e0; cursor:pointer; display:flex; justify-content:space-between; align-items:center; gap:14px; background:rgba(255,255,255,0.02); transition:background 0.15s; }
.faq-q:hover { background:rgba(255,255,255,0.04); }
.faq-q-icon { color:#4a4a4a; font-size:11px; flex-shrink:0; transition:transform 0.2s; }
.faq-q-icon.open { transform:rotate(180deg); color:#0078F2; }
.faq-a { padding:12px 16px; font-size:13px; color:#6b6b6b; line-height:1.7; border-top:1px solid rgba(255,255,255,0.05); background:#1a1a1a; }

/* ── CTA ── */
.htu-cta-wrap { max-width:1400px; margin:0 auto; padding:48px 80px; }
.htu-cta {
  background:linear-gradient(135deg,#0078F2 0%,#0055b3 100%); border-radius:12px;
  padding:40px 60px; display:flex; align-items:center; justify-content:space-between; gap:32px;
  position:relative; overflow:hidden;
}
.htu-cta::before { content:''; position:absolute; right:-50px; top:-50px; width:180px; height:180px; background:rgba(255,255,255,0.05); border-radius:50%; }
.cta-title { font-size:clamp(18px,2.5vw,26px); font-weight:900; color:#fff; letter-spacing:-0.5px; margin-bottom:6px; }
.cta-sub { font-size:14px; color:rgba(255,255,255,0.75); line-height:1.6; max-width:400px; }
.cta-btn { background:#fff; color:#0078F2; font-family:'Inter',sans-serif; font-size:14px; font-weight:800; padding:13px 28px; border-radius:8px; text-decoration:none; white-space:nowrap; flex-shrink:0; transition:all 0.15s; position:relative; z-index:1; }
.cta-btn:hover { background:#f0f0f0; transform:translateY(-1px); }

/* ── Responsive ── */
@media(max-width:1100px) {
  .htu-hero,.vids-wrap,.htu-steps-wrap,.faq-inner,.htu-cta-wrap { padding-left:40px; padding-right:40px; }
  .htu-cta { flex-direction:column; padding:36px 40px; }
}
@media(max-width:760px) {
  .vids-grid { grid-template-columns:1fr; }
  .steps-grid { grid-template-columns:1fr; }
  .step-block { border-right:none !important; }
  .step-block:nth-last-child(2) { border-bottom:1px solid rgba(255,255,255,0.05) !important; }
}
@media(max-width:600px) {
  .htu-hero,.vids-wrap,.htu-steps-wrap,.faq-inner,.htu-cta-wrap { padding-left:20px; padding-right:20px; }
  .htu-title { letter-spacing:-1px; }
}
`;

export default function HowToUse() {
  const { t } = useLang();

  const STEPS = [
    { icon:"🛒", color:"#0078F2", bg:"rgba(0,120,242,0.1)",   title:t("htu_s1_title"), desc:t("htu_s1_desc"), details:[t("htu_s1_d1"),t("htu_s1_d2"),t("htu_s1_d3")] },
    { icon:"📱", color:"#a78bfa", bg:"rgba(167,139,250,0.1)", title:t("htu_s2_title"), desc:t("htu_s2_desc"), details:[t("htu_s2_d1"),t("htu_s2_d2"),t("htu_s2_d3")] },
    { icon:"🔑", color:"#eab308", bg:"rgba(234,179,8,0.1)",   title:t("htu_s3_title"), desc:t("htu_s3_desc"), details:[t("htu_s3_d1"),t("htu_s3_d2"),t("htu_s3_d3")] },
    { icon:"💻", color:"#22c55e", bg:"rgba(34,197,94,0.1)",   title:t("htu_s4_title"), desc:t("htu_s4_desc"), details:[t("htu_s4_d1"),t("htu_s4_d2"),t("htu_s4_d3")] },
    { icon:"🎮", color:"#f97316", bg:"rgba(249,115,22,0.1)",  title:t("htu_s5_title"), desc:t("htu_s5_desc"), details:[t("htu_s5_d1"),t("htu_s5_d2"),t("htu_s5_d3")] },
    { icon:"📴", color:"#06b6d4", bg:"rgba(6,182,212,0.1)",   title:t("htu_s6_title"), desc:t("htu_s6_desc"), details:[t("htu_s6_d1"),t("htu_s6_d2"),t("htu_s6_d3")] },
    { icon:"🏆", color:"#4ade80", bg:"rgba(74,222,128,0.1)",  title:t("htu_s7_title"), desc:t("htu_s7_desc"), details:[t("htu_s7_d1"),t("htu_s7_d2"),t("htu_s7_d3")] },
  ];

  const FAQ = [
    { q:t("htu_faq1_q"), a:t("htu_faq1_a") },
    { q:t("htu_faq2_q"), a:t("htu_faq2_a") },
    { q:t("htu_faq3_q"), a:t("htu_faq3_a") },
    { q:t("htu_faq4_q"), a:t("htu_faq4_a") },
    { q:t("htu_faq5_q"), a:t("htu_faq5_a") },
    { q:t("htu_faq6_q"), a:t("htu_faq6_a") },
    { q:t("htu_faq7_q"), a:t("htu_faq7_a") },
  ];

  return (
    <>
      <style>{S}</style>
      <div className="htu">
        <Navbar />

        {/* HERO */}
        <div className="htu-hero">
          <div className="htu-tag">{t("htu_tag")}</div>
          <h1 className="htu-title">{t("htu_title")} <span>{t("htu_title_accent")}</span></h1>
          <p className="htu-sub">{t("htu_sub")}</p>
        </div>

        {/* VIDEOS — above the guide so mobile users reach them quickly */}
        <div className="vids-wrap">
          <div className="sect-tag-sm">{t("htu_videos_tag")}</div>
          <div className="sect-title-sm">{t("htu_videos_title")}</div>
          <div className="vids-grid">
            <div className="vid-card">
              <div className="vid-label">
                <div className="vid-label-icon" style={{ background:"rgba(0,120,242,0.1)" }}>🛒</div>
                <div>
                  <div className="vid-label-title">{t("htu_vid1_title")}</div>
                  <div className="vid-label-sub">{t("htu_vid1_sub")}</div>
                </div>
              </div>
              <div className="vid-frame-wrap">
                <iframe
                  src="https://www.youtube.com/embed/YJrWm83gVfw?si=tSPFub58htObjj3L"
                  title={t("htu_vid1_title")}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            </div>
            <div className="vid-card">
              <div className="vid-label">
                <div className="vid-label-icon" style={{ background:"rgba(74,222,128,0.1)" }}>🎮</div>
                <div>
                  <div className="vid-label-title">{t("htu_vid2_title")}</div>
                  <div className="vid-label-sub">{t("htu_vid2_sub")}</div>
                </div>
              </div>
              <div className="vid-frame-wrap">
                <iframe
                  src="https://www.youtube.com/embed/zkETI3ITNHc?si=CwavMbVJfxCGtNM5"
                  title={t("htu_vid2_title")}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </div>

        {/* STEP-BY-STEP GUIDE */}
        <div className="htu-steps-bg">
          <div className="htu-steps-wrap">
            <div className="sect-tag-sm">{t("how_tag")}</div>
            <div className="sect-title-sm">{t("how_title")}</div>
            <div className="steps-grid">
              {STEPS.map((s, i) => (
                <div key={i} className="step-block">
                  <div className="step-left">
                    <div className="step-circle" style={{ background:s.bg, borderColor:s.color.replace(")",",0.3)").replace("rgb","rgba") }}>
                      {s.icon}
                    </div>
                    <div className="step-num-label" style={{ color:s.color }}>{String(i+1).padStart(2,"0")}</div>
                  </div>
                  <div className="step-right">
                    <div className="step-title">{s.title}</div>
                    <div className="step-desc">{s.desc}</div>
                    <div className="step-details">
                      {s.details.map((d, j) => (
                        <div key={j} className="step-detail">
                          <span className="step-arrow" style={{ color:s.color }}>→</span> {d}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="faq-wrap">
          <div className="faq-inner">
            <div className="faq-heading">{t("htu_faq_title")}</div>
            {FAQ.map((item, i) => <FAQItem key={i} q={item.q} a={item.a} />)}
          </div>
        </div>

        {/* CTA */}
        <div className="htu-cta-wrap">
          <div className="htu-cta">
            <div>
              <div className="cta-title">{t("htu_cta_title")}</div>
              <p className="cta-sub">{t("htu_cta_sub")}</p>
            </div>
            <Link to="/store" className="cta-btn">{t("cta_btn")}</Link>
          </div>
        </div>
      </div>
    </>
  );
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="faq-item">
      <div className="faq-q" onClick={() => setOpen(!open)}>
        {q}
        <span className={`faq-q-icon ${open ? "open" : ""}`}>▼</span>
      </div>
      {open && <div className="faq-a">{a}</div>}
    </div>
  );
}